import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Loader2,
  Search,
  UploadCloud,
  UserPlus,
} from "lucide-react";
import { request } from "../lib/api";
import { downloadCsvTemplate } from "../lib/csvTemplates";
import { useRowSelection } from "../lib/useRowSelection";
import { secondaryButtonClass } from "../components/ui";
import { useStudyGroups, type GroupOption } from "../lib/useStudyGroups";
import { formatDate } from "../lib/datetime";
import type {
  AdminUser,
  CreatedResponse,
  ImportUsersResponse,
  Notice,
  StructureItem,
  StructureResponse,
  UsersResponse,
} from "../types";
import {
  Badge,
  BulkActionsBar,
  Card,
  ConfirmModal,
  EmptyState,
  ErrorState,
  InlineNotice,
  LoadingState,
  Table,
  errorMessage,
  inputClass,
  labelClass,
  primaryButtonClass,
} from "../components/ui";

type ManagedRole = "teacher" | "facilitator" | "admin" | "super_admin";
const roleLabel: Record<ManagedRole, string> = {
  teacher: "Teacher",
  facilitator: "Facilitator",
  admin: "Admin",
  super_admin: "Super Admin",
};

const NO_SCHOOL = "__none__";

export function Users({ fixedRole }: { fixedRole: ManagedRole }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [listError, setListError] = useState("");
  const [result, setResult] = useState<ImportUsersResponse | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [school, setSchool] = useState("");
  const [editingID, setEditingID] = useState<string | null>(null);
  // Teachers and facilitators can be deleted, and then restored or erased.
  const canDelete = fixedRole === "teacher" || fixedRole === "facilitator";
  const groupOptions = useStudyGroups(fixedRole === "teacher");
  const [assignTo, setAssignTo] = useState("");
  const [view, setView] = useState<"active" | "deleted">("active");
  const showingDeleted = canDelete && view === "deleted";
  const load = useCallback(() => {
    const deleted = showingDeleted ? "&deleted=only" : "";
    request<UsersResponse>(`/api/admin/users?role=${fixedRole}&limit=200${deleted}`)
      .then((data) => {
        setUsers(data.users);
        setListError("");
      })
      .catch((cause) => setListError(errorMessage(cause)));
  }, [fixedRole, showingDeleted]);
  useEffect(() => {
    load();
  }, [load]);
  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const value = await request<ImportUsersResponse>("/api/admin/import/users", {
        method: "POST",
        body: new FormData(event.currentTarget),
      });
      setResult(value);
      load();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }
  const filteredUsers = (users || []).filter(
    (user) =>
      [user.assigned_id, user.name, user.institute, user.center, user.grade]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(query.toLowerCase())) &&
      (!school || (school === NO_SCHOOL ? !user.institute : user.institute === school)) &&
      user.role === fixedRole
  );
  const schoolOptions = [
    ...new Set(
      (users || []).map((user) => user.institute).filter((value): value is string => Boolean(value))
    ),
  ].sort((a, b) => a.localeCompare(b));
  const { selection, selectedIds, clear } = useRowSelection(filteredUsers.map((user) => user.id));
  const [deleting, setDeleting] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false);
  // Delete hides accounts and keeps their data; restore brings them back (still
  // inactive); erase removes a deleted account and all its data for good.
  async function applyToSelected(action: "bulk-delete" | "restore" | "erase") {
    setDeleting(true);
    setBulkError("");
    try {
      await request(`/api/admin/users/${action}`, {
        method: "POST",
        body: JSON.stringify({ ids: [...selectedIds] }),
      });
      setConfirmingBulkDelete(false);
      clear();
      load();
    } catch (cause) {
      setBulkError(errorMessage(cause));
    } finally {
      setDeleting(false);
    }
  }
  const deleteSelected = () => applyToSelected(showingDeleted ? "erase" : "bulk-delete");
  async function assignSelected() {
    setDeleting(true);
    setBulkError("");
    try {
      await request(`/api/admin/study-groups/${assignTo}/assign`, {
        method: "POST",
        body: JSON.stringify({ ids: [...selectedIds] }),
      });
      clear();
      load();
    } catch (cause) {
      setBulkError(errorMessage(cause));
    } finally {
      setDeleting(false);
    }
  }
  return (
    <div className="grid gap-6 min-[1800px]:grid-cols-[minmax(0,1fr)_360px]">
      <Card
        title={`${roleLabel[fixedRole]} accounts`}
        text={`${filteredUsers.length} ${fixedRole} accounts`}
      >
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative min-w-0 flex-1 sm:max-w-sm">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-disabled"
            />
            <input
              className={`${inputClass} pl-9`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={
                fixedRole === "teacher"
                  ? "Search teacher, institute or grade"
                  : `Search ${roleLabel[fixedRole].toLowerCase()} name or assigned ID`
              }
              aria-label="Search users"
            />
          </div>
          {fixedRole === "teacher" && (
            <select
              className={`${inputClass} sm:max-w-xs`}
              value={school}
              onChange={(event) => {
                setSchool(event.target.value);
                clear();
              }}
              aria-label="Filter by school"
            >
              <option value="">All schools</option>
              {schoolOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
              <option value={NO_SCHOOL}>No school assigned</option>
            </select>
          )}
        </div>
        {canDelete && (
          <div className="mb-4 flex gap-2" role="tablist" aria-label="Account view">
            {(["active", "deleted"] as const).map((name) => (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={view === name}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold ${view === name ? "bg-primary text-content-on-primary" : "text-content-secondary hover:bg-surface-muted"}`}
                onClick={() => {
                  clear();
                  setUsers(null);
                  setView(name);
                }}
              >
                {name === "active" ? "Accounts" : "Deleted"}
              </button>
            ))}
          </div>
        )}
        {showingDeleted && (
          <p className="mb-4 text-sm text-content-secondary">
            Deleted accounts cannot sign in and are left out of counts and lists, but what they
            recorded is kept for the study. Restore an account to bring it back (inactive until you
            activate it), or erase it permanently, which also removes all of its data.
          </p>
        )}
        {fixedRole !== "super_admin" && fixedRole !== "admin" && (
          <BulkActionsBar
            count={selectedIds.size}
            onDelete={() => setConfirmingBulkDelete(true)}
            onClear={clear}
            busy={deleting}
            deleteLabel={showingDeleted ? "Erase permanently" : "Delete selected"}
            extra={
              showingDeleted ? (
                <button
                  type="button"
                  className={secondaryButtonClass}
                  onClick={() => void applyToSelected("restore")}
                  disabled={deleting}
                >
                  Restore selected
                </button>
              ) : (
                fixedRole === "teacher" && (
                  <span className="flex items-center gap-2">
                    <select
                      className={`${inputClass} w-56 py-1.5`}
                      value={assignTo}
                      onChange={(event) => setAssignTo(event.target.value)}
                      aria-label="Study group for the selected teachers"
                    >
                      <option value="">Put in group…</option>
                      {groupOptions.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className={secondaryButtonClass}
                      onClick={() => void assignSelected()}
                      disabled={deleting || !assignTo}
                    >
                      Assign
                    </button>
                  </span>
                )
              )
            }
          />
        )}
        {bulkError && <InlineNotice tone="error" text={bulkError} />}
        {listError ? (
          <ErrorState message={listError} />
        ) : users === null ? (
          <LoadingState message="Loading users…" />
        ) : !users.length ? (
          <EmptyState
            title="No users yet"
            message={
              fixedRole === "super_admin"
                ? "The Super Admin account is provisioned during setup."
                : "Create one with the form above, or import a CSV."
            }
          />
        ) : !filteredUsers.length ? (
          <EmptyState title="No matching users" message="Try another search or role filter." />
        ) : (
          <Table
            headers={
              showingDeleted
                ? [
                    { label: "Assigned ID" },
                    { label: "Name", hide: "sm" },
                    { label: "Deleted" },
                    { label: "Deleted by", hide: "sm" },
                  ]
                : fixedRole !== "teacher"
                  ? [
                      { label: "Assigned ID" },
                      { label: "Name" },
                      { label: "Status" },
                      { label: "Actions" },
                    ]
                  : [
                      { label: "Assigned ID" },
                      { label: "Name", hide: "sm" },
                      { label: "Institute", hide: "sm" },
                      { label: "Center", hide: "md" },
                      { label: "Grade", hide: "lg" },
                      { label: "Group" },
                      { label: "Status" },
                      { label: "Actions" },
                    ]
            }
            rows={filteredUsers.map((user) => {
              const identity = (
                <strong key={user.id || user.assigned_id} className="font-semibold">
                  {user.assigned_id}
                </strong>
              );
              const status = (
                <Badge key="status" tone={user.active ? "success" : "neutral"}>
                  {user.active ? "Active" : "Inactive"}
                </Badge>
              );
              const edit = user.id ? (
                <button
                  key="edit"
                  type="button"
                  className="text-xs font-semibold text-interactive hover:underline"
                  onClick={() => setEditingID(user.id!)}
                >
                  Edit
                </button>
              ) : (
                "—"
              );
              if (showingDeleted) {
                return [
                  identity,
                  user.name || "—",
                  user.deleted_at ? formatDate(user.deleted_at) : "—",
                  user.deleted_by || "—",
                ];
              }
              return fixedRole !== "teacher"
                ? [identity, user.name || "—", status, edit]
                : [
                    identity,
                    user.name || "—",
                    user.institute || "—",
                    user.center || "—",
                    user.grade || "—",
                    user.group_name ? (
                      <span key="group">
                        {user.group_name}
                        <span className="block text-xs text-content-secondary">
                          {user.arm_kind}
                        </span>
                      </span>
                    ) : (
                      <span key="group" className="text-xs text-danger">
                        No group
                      </span>
                    ),
                    status,
                    edit,
                  ];
            })}
            selection={fixedRole === "super_admin" || fixedRole === "admin" ? undefined : selection}
          />
        )}
        <ConfirmModal
          open={confirmingBulkDelete}
          title={
            showingDeleted
              ? `Erase ${selectedIds.size} ${fixedRole}${selectedIds.size === 1 ? "" : "s"} permanently?`
              : `Delete ${selectedIds.size} ${fixedRole}${selectedIds.size === 1 ? "" : "s"}?`
          }
          message={
            showingDeleted
              ? "This removes the accounts and everything they recorded: progress, quiz answers, attendance and activity. Use it for a withdrawal. It cannot be undone."
              : "They are signed out and hidden from lists and counts. What they recorded stays for the study, and you can restore them from the Deleted tab."
          }
          confirmLabel={showingDeleted ? "Erase permanently" : "Delete"}
          requireText={showingDeleted ? "erase" : undefined}
          busy={deleting}
          onConfirm={() => void deleteSelected()}
          onCancel={() => setConfirmingBulkDelete(false)}
        />
        {editingID && (
          <EditUserModal
            groups={groupOptions}
            user={users?.find((user) => user.id === editingID) || null}
            onClose={() => setEditingID(null)}
            onSaved={() => {
              setEditingID(null);
              load();
            }}
          />
        )}
      </Card>
      <div>
        {fixedRole !== "super_admin" && <CreateUserForm onCreated={load} fixedRole={fixedRole} />}
        {fixedRole === "teacher" || fixedRole === "facilitator" ? (
          <Card
            title={`Bulk import ${fixedRole}s`}
            text={`Add multiple ${fixedRole} accounts from one CSV file.`}
          >
            <form className="grid gap-4" onSubmit={upload}>
              <div className="rounded-md border border-dashed border-border-strong bg-surface-muted p-4">
                <UploadCloud size={22} className="mb-3 text-interactive" />
                <p className="text-sm font-medium text-content-primary">Choose CSV file</p>
                <p className="mt-1 text-xs leading-5 text-content-secondary">
                  {fixedRole === "teacher"
                    ? "Required: assigned_id, password, role=teacher. Institute and centre codes must match existing records."
                    : "Required: assigned_id, password, role=facilitator. Name is optional."}
                </p>
                <button
                  type="button"
                  className="mt-4 inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-xs font-semibold text-content-primary shadow-sm hover:bg-white"
                  onClick={() => downloadCsvTemplate(`${fixedRole}s`)}
                >
                  <Download size={15} />
                  Download {fixedRole} CSV template
                </button>
                <input
                  className="mt-4 block w-full text-xs text-content-secondary file:mr-3 file:rounded-md file:border file:border-border file:bg-surface file:px-3 file:py-2 file:text-xs file:font-semibold file:text-content-primary hover:file:bg-white"
                  name="file"
                  type="file"
                  accept=".csv,text/csv"
                  required
                />
              </div>
              <button className={primaryButtonClass} disabled={busy}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                Import {fixedRole}s
              </button>
            </form>
            {result && (
              <p className="mt-3 flex items-center gap-1.5 rounded-md bg-success-bg px-3 py-2 text-sm text-success-text">
                <CheckCircle2 size={16} className="shrink-0" />
                {result.accepted} accepted · {result.rejected.length} rejected
              </p>
            )}
            {error && (
              <p className="mt-3 flex items-center gap-1.5 rounded-md bg-danger-bg px-3 py-2 text-sm text-danger">
                <AlertTriangle size={16} className="shrink-0" />
                {error}
              </p>
            )}
          </Card>
        ) : null}
      </div>
    </div>
  );
}

export function Teachers() {
  return <Users fixedRole="teacher" />;
}
export function Facilitators() {
  return <Users fixedRole="facilitator" />;
}
export function AdminAccounts() {
  return (
    <>
      <Users fixedRole="admin" />
      <div className="mt-8">
        <Users fixedRole="super_admin" />
      </div>
    </>
  );
}

function CreateUserForm({
  onCreated,
  fixedRole,
}: {
  onCreated: () => void;
  fixedRole: ManagedRole;
}) {
  const [districts, setDistricts] = useState<StructureItem[]>([]);
  const [centers, setCenters] = useState<StructureItem[]>([]);
  const [institutes, setInstitutes] = useState<StructureItem[]>([]);
  const [districtID, setDistrictID] = useState("");
  const [instituteID, setInstituteID] = useState("");
  const [message, setMessage] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    request<StructureResponse>("/api/admin/structure/districts")
      .then((data) => setDistricts(data.items))
      .catch(() => setDistricts([]));
    request<StructureResponse>("/api/admin/structure/centers")
      .then((data) => setCenters(data.items))
      .catch(() => setCenters([]));
    request<StructureResponse>("/api/admin/structure/institutes")
      .then((data) => setInstitutes(data.items))
      .catch(() => setInstitutes([]));
  }, []);
  const filteredInstitutes = districtID
    ? institutes.filter((institute) => institute.district_id === districtID)
    : institutes;
  const selectedInstitute = institutes.find((institute) => institute.id === instituteID) || null;
  const selectedCenter =
    centers.find((center) => center.id === selectedInstitute?.center_id) || null;
  function selectInstitute(id: string) {
    setInstituteID(id);
    const institute = institutes.find((item) => item.id === id);
    if (institute?.district_id) setDistrictID(institute.district_id);
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    const payload = {
      assigned_id: data.assigned_id,
      name: data.name,
      password: data.password,
      role: fixedRole,
      institute_id: data.institute_id || null,
      center_id: selectedInstitute?.center_id || null,
      grade: data.grade,
      subject: data.subject,
      phone: data.phone,
    };
    try {
      const result = await request<CreatedResponse>("/api/admin/users", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setMessage({ tone: "success", text: `User created: ${result.id}` });
      form.reset();
      setDistrictID("");
      setInstituteID("");
      onCreated();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card
      title={`Create ${roleLabel[fixedRole]}`}
      text={
        fixedRole === "facilitator"
          ? "Add a facilitator account for running assigned PD days."
          : fixedRole === "teacher"
            ? "Add a teacher account and assign its school profile."
            : fixedRole === "admin"
              ? "Grant module and content management access."
              : "Grant full portal access."
      }
    >
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}>
        <label className={labelClass}>
          Assigned ID
          <input className={inputClass} name="assigned_id" required />
        </label>
        <label className={labelClass}>
          Name
          <input className={inputClass} name="name" required={false} />
        </label>
        <label className={labelClass}>
          Password
          <input className={inputClass} name="password" type="password" required minLength={4} />
        </label>

        {fixedRole === "teacher" && (
          <label className={labelClass}>
            District (filter / auto-filled from institute)
            <select
              className={inputClass}
              value={districtID}
              onChange={(event) => {
                setDistrictID(event.target.value);
                setInstituteID("");
              }}
            >
              <option value="">All districts</option>
              {districts.map((district) => (
                <option key={district.id} value={district.id}>
                  {district.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {fixedRole === "teacher" && (
          <>
            <label className={labelClass}>
              Institute
              <select
                className={inputClass}
                name="institute_id"
                value={instituteID}
                onChange={(event) => selectInstitute(event.target.value)}
              >
                <option value="">Select institute</option>
                {filteredInstitutes.map((institute) => (
                  <option key={institute.id} value={institute.id}>
                    {institute.name}
                  </option>
                ))}
              </select>
            </label>
            <label className={labelClass}>
              Center
              <input
                className={`${inputClass} bg-surface-muted text-content-secondary`}
                value={selectedCenter?.name || "Auto-filled from institute"}
                readOnly
                tabIndex={-1}
              />
            </label>
            <label className={labelClass}>
              Grade
              <input className={inputClass} name="grade" required={false} />
            </label>
            <label className={labelClass}>
              Subject
              <input className={inputClass} name="subject" required={false} />
            </label>
            <label className={labelClass}>
              Phone
              <input className={inputClass} name="phone" required={false} />
            </label>
          </>
        )}
        <button className={`${primaryButtonClass} sm:col-span-2`} disabled={busy}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
          Create {roleLabel[fixedRole]}
        </button>
      </form>
      {message && <InlineNotice tone={message.tone} text={message.text} breakAll />}
    </Card>
  );
}

function EditUserModal({
  user,
  groups,
  onClose,
  onSaved,
}: {
  user: AdminUser | null;
  groups: GroupOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [active, setActive] = useState(user?.active ?? true);
  const [name, setName] = useState(user?.name || "");
  const [password, setPassword] = useState("");
  const [groupID, setGroupID] = useState(user?.group_id || "");
  const [message, setMessage] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  if (!user) return null;
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    try {
      // The group goes first: it is the change the server may refuse (a locked
      // cohort), and nothing else should be saved if so.
      if (user!.role === "teacher" && groupID !== (user!.group_id || "")) {
        await request(`/api/admin/users/${user!.id}/group`, {
          method: "PUT",
          body: JSON.stringify(
            groupID ? { group_id: groupID } : { group_id: null, cohort_id: user!.group_cohort_id }
          ),
        });
      }
      await request<CreatedResponse>(`/api/admin/users/${user!.id}`, {
        method: "PUT",
        body: JSON.stringify({ active, name, password: password || null }),
      });
      onSaved();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4 backdrop-blur-sm md:left-64">
      <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-5 shadow-lg">
        <h3 className="text-base font-semibold text-content-primary">Edit {user.assigned_id}</h3>
        <form className="mt-4 grid gap-4" onSubmit={submit}>
          <label className={labelClass}>
            Name
            <input
              className={inputClass}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          {user.role === "teacher" && (
            <label className={labelClass}>
              Study group
              <select
                className={inputClass}
                value={groupID}
                onChange={(event) => setGroupID(event.target.value)}
              >
                <option value="">No group</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.label}
                  </option>
                ))}
              </select>
              <span className="text-xs font-normal text-content-secondary">
                Once the cohort's assignments are locked, a group cannot be changed.
              </span>
            </label>
          )}
          <label className="flex items-center gap-2 text-sm font-medium text-content-primary">
            <input
              className="h-4 w-4 accent-interactive"
              type="checkbox"
              checked={active}
              onChange={(event) => setActive(event.target.checked)}
              disabled={user.role === "super_admin"}
            />
            {user.role === "super_admin" ? "Active (required)" : "Active"}
          </label>
          <label className={labelClass}>
            New password (optional)
            <input
              className={inputClass}
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={4}
              placeholder="Leave blank to keep current"
            />
          </label>
          <div className="flex items-center gap-3">
            <button className={primaryButtonClass} disabled={busy}>
              {busy && <Loader2 size={16} className="animate-spin" />}Save
            </button>
            <button
              type="button"
              className="text-sm font-medium text-content-secondary hover:text-content-primary"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </form>
        {message && <InlineNotice tone={message.tone} text={message.text} />}
      </div>
    </div>
  );
}
